import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  ChangeDetectorRef,
  SimpleChanges,
} from '@angular/core';
import * as L from 'leaflet';
import 'leaflet.pm';

export interface ZoneData {
  type: 'Polygon' | 'Circle';
  coordinates?: L.LatLngExpression[];
  center?: L.LatLngExpression;
  radius?: number;
  centroid: L.LatLngExpression;
}

export interface ParentZone {
  id: string;
  name: string;
  type: 'Polygon' | 'Circle';
  coordinates?: L.LatLngExpression[];
  center?: L.LatLngExpression;
  radius?: number;
}

@Component({
  selector: 'app-geo-fencing-zone-drawer',
  templateUrl: './geo-fencing-zone-drawer.component.html',
  styleUrls: ['./geo-fencing-zone-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class GeoFencingZoneDrawerComponent implements OnInit, OnDestroy {
  @Input() defaultLocation: L.LatLngExpression = [8.534796, 76.884992];
  @Input() parentZones: ParentZone[] = [];
  @Input() mapHeight: string = '200px';
  @Input() zoomLevel: number = 13;
  @Input() readonly: boolean = false;
  @Input() existingZoneData: ZoneData | null = null;
  @Input() isViewMode: boolean = false;
  @Input() showPolygonInfo: boolean = true;

  @Output() zoneDrawn = new EventEmitter<ZoneData>();
  @Output() zoneCleared = new EventEmitter<void>();

  public hasActiveDrawing = false;
  public currentZoneData: ZoneData | null = null;
  public mapId = `geo-fence-map-${Date.now()}`;

  private map!: L.Map | any;
  private currentLayer: L.Layer | null = null;
  private parentLayers: Map<string, L.Layer> = new Map();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    setTimeout(() => this.initializeMap(), 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Watch for changes in existingZoneData
    if (changes['existingZoneData'] && this.map) {
      if (changes['existingZoneData'].currentValue) {
        this.restoreZoneFromData(changes['existingZoneData'].currentValue);
      } else if (
        changes['existingZoneData'].previousValue &&
        !changes['existingZoneData'].currentValue
      ) {
        // Clear the zone if existingZoneData becomes null
        this.clearDrawing();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.off();
      this.map.remove();
    }
  }

  /**
   * Initialize the map with basic setup
   */
  private initializeMap(): void {
    // Create tile layer
    const osmTile = L.tileLayer(
      'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19,
      }
    );

    // Initialize map
    this.map = L.map(this.mapId, {
      layers: [osmTile],
      zoomControl: true,
    }).setView(this.defaultLocation, this.zoomLevel);

    // Load parent zones (read-only)
    this.loadParentZones();

    // Setup drawing events
    this.setupDrawingEvents();
    // Restore existing zone data if available
    if (this.existingZoneData) {
      setTimeout(() => this.restoreZoneFromData(this.existingZoneData!), 500);
    }
  }

  /**
   * Restore zone from existing data
   */
  private restoreZoneFromData(zoneData: ZoneData): void {
    if (!this.map) return;

    // Clear existing drawing first
    if (this.currentLayer) {
      this.map.removeLayer(this.currentLayer);
      this.currentLayer = null;
    }

    let layer: L.Layer | any = null;

    try {
      if (zoneData.type === 'Circle' && zoneData.center && zoneData.radius) {
        // Create circle
        const center = Array.isArray(zoneData.center)
          ? L.latLng(zoneData.center[0], zoneData.center[1])
          : (zoneData.center as L.LatLng);

        layer = L.circle(center, {
          radius: zoneData.radius,
          color: '#2196F3',
          fillColor: '#2196F3',
          fillOpacity: 0.3,
          weight: 2,
        });
      } else if (
        zoneData.type === 'Polygon' &&
        zoneData.coordinates &&
        zoneData.coordinates.length > 0
      ) {
        // Create polygon
        const coordinates = zoneData.coordinates.map((coord) => {
          if (Array.isArray(coord)) {
            return L.latLng(coord[0], coord[1]);
          } else if (
            typeof coord === 'object' &&
            'lat' in coord &&
            'lng' in coord
          ) {
            return coord as L.LatLng;
          }
          return L.latLng(0, 0); // fallback
        });

        layer = L.polygon(coordinates, {
          color: '#4CAF50',
          fillColor: '#4CAF50',
          fillOpacity: 0.3,
          weight: 2,
        });
      }

      if (layer) {
        this.currentLayer = layer;
        layer.addTo(this.map);
        this.hasActiveDrawing = true;
        this.currentZoneData = zoneData;

        // Fit map bounds to the restored shape
        this.map.fitBounds(layer.getBounds(), { padding: [20, 20] });

        // Trigger change detection
        this.cdr.detectChanges();
      }
    } catch (error) {
      console.error('Error restoring zone data:', error);
    }
  }

  /**
   * Load parent zones as read-only layers
   */
  private loadParentZones(): void {
    this.parentZones.forEach((zone) => {
      let layer: L.Layer;

      if (zone.type === 'Circle' && zone.center && zone.radius) {
        layer = L.circle(zone.center, {
          radius: zone.radius,
          color: '#ff6b6b',
          fillColor: '#ff6b6b',
          fillOpacity: 0.1,
          weight: 2,
          dashArray: '5, 5',
        });
      } else if (zone.type === 'Polygon' && zone.coordinates) {
        layer = L.polygon(zone.coordinates, {
          color: '#ff6b6b',
          fillColor: '#ff6b6b',
          fillOpacity: 0.1,
          weight: 2,
          dashArray: '5, 5',
        });
      } else {
        return;
      }

      // Add tooltip
      layer.bindTooltip(`Parent Zone: ${zone.name}`, {
        permanent: false,
        direction: 'center',
      });

      // Mark as parent zone (read-only)
      (layer as any)._isParentZone = true;
      (layer as any)._zoneId = zone.id;
      (layer as any).options.pmIgnore = true;

      layer.addTo(this.map);
      this.parentLayers.set(zone.id, layer);
    });
  }

  /**
   * Setup drawing event listeners
   */
  private setupDrawingEvents(): void {
    this.map.on('pm:create', (e: any) => {
      // Remove previous drawing if exists
      if (this.currentLayer) {
        this.map.removeLayer(this.currentLayer);
      }

      this.currentLayer = e.layer;
      this.hasActiveDrawing = true;

      // Calculate zone data
      const zoneData = this.calculateZoneData(e.layer, e.shape);
      if (zoneData) {
        this.currentZoneData = zoneData;
        this.zoneDrawn.emit(zoneData);
        // Trigger change detection to update the UI
        this.cdr.detectChanges();
      }

      // Disable drawing after creation
      this.map.pm.disableDraw();
    });

    // Prevent parent zone modification
    this.map.on(
      'pm:dragstart pm:editstart pm:rotatestart pm:remove',
      (e: any) => {
        if ((e.layer as any)._isParentZone) {
          e.preventDefault();
          return false;
        }
        return true;
      }
    );
    if (!this.showPolygonInfo) {
      this.updateZoomControl();
    }
  }

  /**
   * Enable polygon drawing mode
   */
  public enablePolygonDrawing(): void {
    if (this.readonly) return;

    // Clear existing drawing first if any
    if (this.hasActiveDrawing) {
      this.clearDrawing();
    }

    this.map.pm.enableDraw('Polygon', {
      snappable: false,
      snapDistance: 20,
      allowSelfIntersection: false,
      templineStyle: {
        color: '#4CAF50',
        weight: 3,
      },
      hintlineStyle: {
        color: '#4CAF50',
        dashArray: '5, 5',
      },
    });
  }

  /**
   * Enable circle drawing mode
   */
  public enableCircleDrawing(): void {
    if (this.readonly) return;

    // Clear existing drawing first if any
    if (this.hasActiveDrawing) {
      this.clearDrawing();
    }

    this.map.pm.enableDraw('Circle', {
      snappable: false,
      templineStyle: {
        color: '#2196F3',
        weight: 3,
      },
    });
  }

  /**
   * Clear current drawing
   */
  public clearDrawing(): void {
    if (this.currentLayer) {
      this.map.removeLayer(this.currentLayer);
      this.currentLayer = null;
    }

    this.hasActiveDrawing = false;
    this.currentZoneData = null;
    this.zoneCleared.emit();
    // Trigger change detection to update the UI
    this.cdr.detectChanges();
  }

  /**
   * Calculate zone data from drawn layer
   */
  private calculateZoneData(layer: L.Layer, shape: string): ZoneData | null {
    try {
      if (shape === 'Circle' && layer instanceof L.Circle) {
        const center = layer.getLatLng();
        const radius = layer.getRadius();

        // Style the circle
        layer.setStyle({
          color: '#2196F3',
          fillColor: '#2196F3',
          fillOpacity: 0.3,
          weight: 2,
        });

        return {
          type: 'Circle',
          center: [center.lat, center.lng],
          radius: radius,
          centroid: [center.lat, center.lng],
        };
      } else if (shape === 'Polygon' && layer instanceof L.Polygon) {
        const coordinates = layer.getLatLngs()[0] as L.LatLng[];
        const latLngs = coordinates.map(
          (coord) => [coord.lat, coord.lng] as L.LatLngExpression
        );
        const centroid = this.calculatePolygonCentroid(coordinates);

        // Style the polygon
        layer.setStyle({
          color: '#4CAF50',
          fillColor: '#4CAF50',
          fillOpacity: 0.3,
          weight: 2,
        });

        return {
          type: 'Polygon',
          coordinates: latLngs,
          centroid: [centroid.lat, centroid.lng],
        };
      }
    } catch (error) {
      console.error('Error calculating zone data:', error);
    }

    return null;
  }

  /**
   * Calculate polygon centroid using shoelace formula
   */
  private calculatePolygonCentroid(coordinates: L.LatLng[]): L.LatLng {
    let area = 0;
    let centroidLat = 0;
    let centroidLng = 0;

    const n = coordinates.length;

    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const xi = coordinates[i].lat;
      const yi = coordinates[i].lng;
      const xj = coordinates[j].lat;
      const yj = coordinates[j].lng;

      const a = xi * yj - xj * yi;
      area += a;
      centroidLat += (xi + xj) * a;
      centroidLng += (yi + yj) * a;
    }

    area *= 0.5;
    centroidLat /= 6 * area;
    centroidLng /= 6 * area;

    return L.latLng(Math.abs(centroidLat), Math.abs(centroidLng));
  }

  /**
   * Format coordinate for display
   */
  public formatCoordinate(coord: L.LatLngExpression): string {
    if (Array.isArray(coord)) {
      return `${coord[0].toFixed(6)}, ${coord[1].toFixed(6)}`;
    }
    return `${(coord as any).lat?.toFixed(6)}, ${(coord as any).lng?.toFixed(
      6
    )}`;
  }

  /**
   * Format radius for display
   */
  public formatRadius(radius: number): string {
    if (radius < 1000) {
      return `${Math.round(radius)} meters`;
    }
    return `${(radius / 1000).toFixed(2)} km`;
  }
  /**
   * Update zoom control based on currentZoneData availability
   */
  private updateZoomControl(): void {
    // Disable zoom when zone data is available
    this.map.zoomControl.remove();
    this.map.touchZoom.disable();
    this.map.doubleClickZoom.disable();
    this.map.scrollWheelZoom.disable();
    this.map.boxZoom.disable();
    this.map.keyboard.disable();
  }
}
